// components/OldTenthStudents.jsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import PaymentsIcon from "@mui/icons-material/Payments";
import BadgeIcon from "@mui/icons-material/Badge";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {useAuth} from "../../../../AuthProvider"

const baseUrl = process.env.REACT_APP_API_BASE_URL;

// ---- Helpers ----
const displayValue = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  return val;
};

const formatDate = (arr) => {
  if (!Array.isArray(arr) || arr.length < 3) return "—";
  const [y, m, d] = arr;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusColorMap = {
  studentPresent: { bg: "#e8f5e9", color: "#2e7d32", label: "Present" },
  studentAbsent: { bg: "#ffebee", color: "#c62828", label: "Absent" },
};

const StatusChip = ({ status }) => {
  const info = statusColorMap[status] || {
    bg: "#f1f5f9",
    color: "#475569",
    label: status || "—",
  };
  return (
    <Chip
      label={info.label}
      size="small"
      sx={{
        bgcolor: info.bg,
        color: info.color,
        fontWeight: 600,
        border: `1px solid ${info.color}33`,
      }}
    />
  );
};

// A single label/value block used inside the detail dialog
const DetailRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1,
      px: 1.5,
      borderRadius: 1.5,
      "&:hover": { bgcolor: "#f8fafc" },
    }}
  >
    <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "#1e293b", fontWeight: 600, textAlign: "right" }}
    >
      {displayValue(value)}
    </Typography>
  </Box>
);

// Section wrapper used inside the detail dialog
const SectionCard = ({ icon, title, accentColor, children }) => (
  <Box
    sx={{
      borderRadius: 3,
      border: "1px solid #e2e8f0",
      overflow: "hidden",
      mb: 2.5,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1.2,
        bgcolor: `${accentColor}12`,
        borderBottom: `1px solid ${accentColor}30`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "8px",
          bgcolor: accentColor,
          color: "#fff",
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color: "#1e293b" }}
      >
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 1, py: 0.5 }}>{children}</Box>
  </Box>
);

const OldTenthStudents = () => {
  const { oId } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const fetchOldTenthStudents = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/api/studentPromotion/class11-students/${oId}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch students");
      }
      const data = await response.json();
      setStudents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching class11-students:", error);
      toast.error("Failed to load promoted students.", { autoClose: 2000 });
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOldTenthStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oId]);

  const filteredStudents = useMemo(() => {
    if (!searchQuery) return students;
    const term = searchQuery.toLowerCase();
    return students.filter((s) =>
      [
        s.customAdmissionNumber,
        s.admissionNumber,
        s.firstName,
        s.lastName,
        s.primaryContact,
        s.studentId,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [searchQuery, students]);

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setViewDialogOpen(true);
  };

  const getInitials = (student) =>
    `${student.firstName?.[0] || ""}${student.lastName?.[0] || ""}`.toUpperCase();

  return (
    <Box sx={{ mt: "5%", mr: "1%", ml: "1%" }}>
      <Card
        sx={{
          marginTop: "60px",
          width: "100%",
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography
                variant="h4"
                component="h3"
                sx={{ fontWeight: 700, color: "#1e293b" }}
              >
                Old 10th Class Students
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                Students previously in 10th class.
              </Typography>
            </Box>

            <TextField
              placeholder="Search by name, admission no, contact..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

        {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              border: "1px solid #94a3b8",
              boxShadow: "none",
              overflow: "hidden",
            }}
          >
            <Table
              sx={{
                borderCollapse: "collapse",
                "& td, & th": {
                  border: "1px solid #cbd5e1",
                  padding: "6px 10px",
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#fff9c4" }}>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: "#334155",
                      borderTop: "none",
                      width: 60,
                    }}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, color: "#334155", width: 110 }}
                  >
                    Admission No
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                    Student Name
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                    Father's Name
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                    Contact
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                    Class &amp; Section
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        No students found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map((student, index) => (
                    <TableRow key={student.admissionNumber} hover>
                      <TableCell align="center">
                        <Typography sx={{ color: "#64748b", fontWeight: 600 }}>
                          {index + 1}
                        </Typography>
                      </TableCell>

                      <TableCell align="center" sx={{ width: 110 }}>
                        <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                          {displayValue(
                            student.customAdmissionNumber ??
                              student.admissionNumber,
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                          }}
                        >
                          {/* <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: 12,
                              fontWeight: 700,
                              bgcolor: "#e0e7ff",
                              color: "#4338ca",
                            }}
                          >
                            {getInitials(student)}
                          </Avatar> */}
                          <Box sx={{ textAlign: "left" }}>
                            <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                              {displayValue(student.firstName)}{" "}
                              {displayValue(student.lastName)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                              {student.studentId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Typography sx={{ color: "#334155" }}>
                          {displayValue(student.fathersFirstName)}{" "}
                          {displayValue(student.fathersLastName)}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography sx={{ color: "#334155" }}>
                          {displayValue(student.primaryContact)}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={`${displayValue(student.romanNumberClass)} • ${displayValue(
                            student.sections,
                          )}`}
                          size="small"
                          sx={{
                            bgcolor: "#eff6ff",
                            color: "#1d4ed8",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewStudent(student)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            borderColor: "#cbd5e1",
                            color: "#334155",
                            py: 0.3,
                            "&:hover": {
                              borderColor: "#1976d2",
                              bgcolor: "#eff6ff",
                              color: "#1976d2",
                            },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* View Details Dialog */}
          <Dialog
            open={viewDialogOpen}
            onClose={() => setViewDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 4, overflow: "hidden" },
            }}
          >
            {selectedStudent && (
              <>
                <DialogTitle
                  sx={{
                    background: "linear-gradient(135deg, #3C3972, #42a5f5)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: "rgba(255,255,255,0.25)",
                        fontWeight: 700,
                      }}
                    >
                      {getInitials(selectedStudent)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedStudent.firstName} {selectedStudent.lastName}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        {selectedStudent.studentId}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => setViewDialogOpen(false)}
                    sx={{ color: "#fff" }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3, bgcolor: "#f8fafc" }}>
                  {/* Quick status row */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 2.5,
                      mt: 1,
                    }}
                  >
                    {/* <StatusChip status={selectedStudent.status} /> */}
                    <Chip
                      icon={<SchoolIcon sx={{ fontSize: 16 }} />}
                      label={`${selectedStudent.romanNumberClass || "—"} • ${
                        selectedStudent.sections || "—"
                      }`}
                      size="small"
                      sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 600 }}
                    />
                    <Chip
                      label={`Roll No: ${displayValue(
                        selectedStudent.studentRollNumber,
                      )}`}
                      size="small"
                      sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600 }}
                    />
                  </Box>

                  {/* Personal Info */}
                  <SectionCard
                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                    title="Information"
                    accentColor="#1976d2"
                  >
                    <DetailRow
                      label="Admission Number"
                      value={
                        selectedStudent.customAdmissionNumber ??
                        selectedStudent.admissionNumber
                      }
                    />
                    <Divider />
                    <DetailRow label="Gender" value={selectedStudent.gender} />
                    <Divider />
                    <DetailRow
                      label="Father's Name"
                      value={`${displayValue(
                        selectedStudent.fathersFirstName,
                      )} ${displayValue(selectedStudent.fathersLastName)}`}
                    />
                      <Divider />
                    <DetailRow
                      label="Date of Birth"
                      value={
                        selectedStudent.dob ? formatDate(selectedStudent.dob) : "—"
                      }
                    />
                    <Divider />
                      <DetailRow
                      label="Academic Year"
                      value={selectedStudent.academicYear}
                    />
                    <Divider />
                    <DetailRow
                      label="Student Joined Year"
                      value={selectedStudent.studentJoinedYear}
                    />
                    <Divider />
                     <DetailRow
                      label="Tuition Fee"
                      value={selectedStudent.tutionFee}
                    />
                    <Divider />
                    <DetailRow
                      label="Admission Fee"
                      value={selectedStudent.admissionFee}
                    />
                   
                    <DetailRow label="Address" value={selectedStudent.address} />
                  </SectionCard>

                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, bgcolor: "#f8fafc" }}>
                  <Button
                    onClick={() => setViewDialogOpen(false)}
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 4,
                      background: "linear-gradient(135deg, #3C3972, #42a5f5)",
                    }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          <ToastContainer position="top-right" autoClose={2000} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default OldTenthStudents;